import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, setAdminToken, resolveImg } from "@/lib/api";
import { LANGUAGES } from "@/i18n/translations";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, BarChart, Bar, CartesianGrid } from "recharts";
import { MediaLibrary, SiteImages, MediaDropzone } from "@/components/admin/Media";
import { Coupons } from "@/components/admin/Coupons";
import { Trash2 } from "lucide-react";

export default function AdminPage() {
  const [token, setTokenState] = useState(sessionStorage.getItem("sr_admin_token"));
  const [tab, setTab] = useState("dashboard");

  if (!token) return <AdminLogin onLogin={(tk) => { setAdminToken(tk); setTokenState(tk); }} />;

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <header className="border-b border-gold px-6 py-4 flex items-center justify-between sticky top-0 z-40 bg-obsidian/95 backdrop-blur-xl">
        <div className="font-serif-lux text-sm tracking-[0.3em] text-gold-hi">SHREE RAGO ADMIN</div>
        <button
          onClick={() => { setAdminToken(null); setTokenState(null); }}
          className="text-xs uppercase tracking-widest text-muted-warm hover:text-gold-hi"
          data-testid="admin-logout-btn"
        >
          Logout
        </button>
      </header>

      <div className="flex flex-col md:flex-row max-w-7xl mx-auto">
        <nav className="md:w-56 md:border-r border-gold p-4 flex md:flex-col gap-2 overflow-x-auto md:overflow-visible">
          {["dashboard","orders","images","site-images","testimonials","media","coupons","campaigns"].map((k) => (
            <button
              key={k}
              data-testid={`admin-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-4 py-2.5 rounded-lg text-xs uppercase tracking-widest text-left whitespace-nowrap ${
                tab === k ? "bg-card-elev border border-gold-active text-gold-hi" : "text-muted-warm hover:text-gold-hi"
              }`}
            >
              {k.replace("-", " ")}
            </button>
          ))}
        </nav>

        <main className="flex-1 p-4 sm:p-6">
          {tab === "dashboard" && <Dashboard />}
          {tab === "orders" && <Orders />}
          {tab === "images" && <Images />}
          {tab === "site-images" && <SiteImages />}
          {tab === "testimonials" && <Testimonials />}
          {tab === "media" && <MediaLibrary />}
          {tab === "coupons" && <Coupons />}
          {tab === "campaigns" && <Campaigns />}
        </main>
      </div>
    </div>
  );
}

function AdminLogin({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/login", { email, password });
      onLogin(data.token);
      toast.success("Welcome, admin");
    } catch {
      toast.error("Invalid credentials");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-obsidian flex items-center justify-center px-6">
      <form onSubmit={submit} className="glass-panel rounded-3xl p-8 max-w-sm w-full" data-testid="admin-login-form">
        <div className="text-center mb-8">
          <div className="font-serif-lux text-xl tracking-[0.35em] text-gold-hi">SHREE RAGO</div>
          <div className="text-xs uppercase tracking-widest text-muted-warm mt-1">Admin Portal</div>
        </div>
        <label className="block mb-4">
          <span className="text-xs uppercase text-gold tracking-widest">Email</span>
          <input data-testid="admin-email-input" value={email} onChange={(e)=>setEmail(e.target.value)} type="email" className="w-full mt-2 bg-card-warm border border-gold rounded-lg px-4 py-3 text-ivory" required />
        </label>
        <label className="block mb-6">
          <span className="text-xs uppercase text-gold tracking-widest">Password</span>
          <input data-testid="admin-password-input" value={password} onChange={(e)=>setPassword(e.target.value)} type="password" className="w-full mt-2 bg-card-warm border border-gold rounded-lg px-4 py-3 text-ivory" required />
        </label>
        <button data-testid="admin-login-btn" disabled={busy} className="btn-gold w-full text-sm disabled:opacity-60">{busy ? "…" : "Sign In"}</button>
      </form>
    </div>
  );
}

function PaymentStatus() {
  const [st, setSt] = useState(null);
  useEffect(() => { api.get("/admin/payments/status").then((r) => setSt(r.data)).catch(() => {}); }, []);
  if (!st) return null;
  const ok = st.keys_configured && st.key_type === "live";
  return (
    <div className={`rounded-2xl border px-4 py-3 mb-6 flex flex-wrap items-center gap-3 text-xs ${ok ? "border-green-800/60 bg-green-900/20 text-green-200" : "border-red-800/60 bg-red-900/20 text-red-200"}`} data-testid="payment-status">
      <span className={`w-2 h-2 rounded-full ${ok ? "bg-green-400" : "bg-red-400"}`} />
      <span className="uppercase tracking-widest">Razorpay</span>
      <span data-testid="payment-status-text">
        {st.keys_configured ? `${st.key_type.toUpperCase()} keys active (${st.key_id_masked})` : "KEYS MISSING — set RAZORPAY_KEY_ID / RAZORPAY_KEY_SECRET in Secrets"}
      </span>
      <span className="text-muted-warm">· Webhook {st.webhook_configured ? "configured" : "not set (optional)"}</span>
    </div>
  );
}

function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="text-muted-warm">Loading…</div>;

  const cards = [
    { label: "Today Revenue", value: `₹${stats.cards.today_revenue}` },
    { label: "Today Paid", value: stats.cards.today_paid },
    { label: "Today Leads", value: stats.cards.today_leads },
    { label: "Total Revenue", value: `₹${stats.cards.total_revenue}` },
    { label: "Total Paid", value: stats.cards.total_paid },
    { label: "Conversion", value: `${stats.cards.conversion_rate}%` },
  ];
  const funnel = [
    { stage: "Visitors", v: stats.funnel.visitors },
    { stage: "Form Start", v: stats.funnel.form_starts },
    { stage: "Form Complete", v: stats.funnel.form_completes },
    { stage: "Payment Click", v: stats.funnel.payment_clicks },
    { stage: "Paid", v: stats.funnel.payment_success },
    { stage: "Downloads", v: stats.funnel.downloads },
  ];

  return (
    <div className="space-y-8">
      <PaymentStatus />
      <div>
        <h2 className="font-serif-lux text-xl text-gold-hi mb-4">Today Snapshot</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          {cards.map((c) => (
            <div key={c.label} className="glass-panel rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-widest text-muted-warm">{c.label}</div>
              <div className="font-serif-lux text-xl text-gold-hi mt-1">{c.value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-4 sm:p-6">
        <h3 className="font-serif-lux text-lg text-gold-hi mb-4">Conversion Funnel</h3>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={funnel}>
              <CartesianGrid stroke="#2a2118" strokeDasharray="3 3" />
              <XAxis dataKey="stage" stroke="#B9AE9C" fontSize={11} />
              <YAxis stroke="#B9AE9C" fontSize={11} />
              <Tooltip contentStyle={{ background: "#171410", border: "1px solid #D6AA45" }} />
              <Bar dataKey="v" fill="#D6AA45" radius={[4,4,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="glass-panel rounded-2xl p-4 sm:p-6">
          <h3 className="font-serif-lux text-lg text-gold-hi mb-4">Language Performance</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-muted-warm">
              <tr><th className="text-left py-2">Lang</th><th>Leads</th><th>Paid</th><th>Revenue</th><th>Conv%</th></tr>
            </thead>
            <tbody className="text-ivory">
              {stats.language_performance.map((l) => (
                <tr key={l.language} className="border-t border-gold/20">
                  <td className="py-2">{l.language}</td>
                  <td className="text-center">{l.leads}</td>
                  <td className="text-center">{l.paid}</td>
                  <td className="text-center text-gold-hi">₹{l.revenue}</td>
                  <td className="text-center">{l.conversion}%</td>
                </tr>
              ))}
              {stats.language_performance.length === 0 && (
                <tr><td colSpan={5} className="text-center py-4 text-muted-warm">No data yet</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="glass-panel rounded-2xl p-4 sm:p-6">
          <h3 className="font-serif-lux text-lg text-gold-hi mb-4">Number Performance (1-9)</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <BarChart data={stats.number_performance}>
                <XAxis dataKey="number" stroke="#B9AE9C" />
                <YAxis stroke="#B9AE9C" />
                <Tooltip contentStyle={{ background: "#171410", border: "1px solid #D6AA45" }} />
                <Bar dataKey="paid" fill="#F2D27A" />
                <Bar dataKey="leads" fill="#8A6420" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/orders").then(r => setRows(r.data)); }, []);
  return (
    <div className="glass-panel rounded-2xl p-4 overflow-x-auto">
      <h2 className="font-serif-lux text-xl text-gold-hi mb-4">Orders & Leads</h2>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase text-muted-warm">
          <tr><th className="text-left py-2">Name</th><th>DOB</th><th>Place</th><th>Lang</th><th>Number</th><th>Deity</th><th>Status</th></tr>
        </thead>
        <tbody className="text-ivory">
          {rows.map((r) => (
            <tr key={r.id} className="border-t border-gold/20">
              <td className="py-2">{r.full_name}</td>
              <td>{r.dob}</td>
              <td>{r.birth_place}</td>
              <td className="uppercase text-xs">{r.language}</td>
              <td className="text-center">{r.life_path}</td>
              <td className="text-gold-hi">{r.assigned_deity}</td>
              <td>
                <span className={`text-xs px-2 py-1 rounded ${r.payment_status === "paid" ? "bg-green-900/40 text-green-400" : "bg-yellow-900/30 text-yellow-300"}`}>
                  {r.payment_status}
                </span>
              </td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-warm">No leads yet</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Images() {
  const [imgs, setImgs] = useState([]);
  const [filter, setFilter] = useState(0);
  const [editing, setEditing] = useState(null);
  const [previewing, setPreviewing] = useState(null);
  const load = useCallback(() => api.get("/admin/images").then((r) => setImgs(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (id, active) => {
    await api.patch(`/admin/images/${id}`, { active: !active });
    load();
  };
  const shown = filter ? imgs.filter((i) => i.numerology_category === filter) : imgs;
  const uploaded = imgs.filter((i) => i.source === "upload").length;
  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-serif-lux text-xl text-gold-hi">Divine Image Library</h2>
          <p className="text-xs text-muted-warm mt-1" data-testid="image-library-progress">
            {uploaded} of {imgs.length} slots replaced with your artwork · Upload 9:16 JPG/PNG/WEBP, min 1280px tall, max 10 MB
          </p>
        </div>
        <div className="flex gap-1 flex-wrap" data-testid="image-number-filter">
          {[0,1,2,3,4,5,6,7,8,9].map((n) => (
            <button key={n} data-testid={`image-filter-${n}`} onClick={() => setFilter(n)}
              className={`text-[11px] px-2.5 py-1 rounded ${filter === n ? "bg-gold text-obsidian" : "border border-gold/40 text-muted-warm hover:text-gold-hi"}`}>
              {n === 0 ? "All" : n}
            </button>
          ))}
        </div>
      </div>
      <BulkUploadZone imgs={imgs} defaultNumber={filter || 1} onDone={load} />
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        {shown.map((i) => (
          <ImageSlotCard key={i.id} img={i} onToggle={() => toggle(i.id, i.active)} onEdit={() => setEditing(i)} onPreview={() => setPreviewing(i)} onChanged={load} />
        ))}
      </div>
      {editing && <ImageEditModal img={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {previewing && <PhonePreviewModal img={previewing} onClose={() => setPreviewing(null)} />}
    </div>
  );
}

const ASPECT_OK = (w, h) => Math.abs(w / h - 9 / 16) / (9 / 16) <= 0.05;
const readDims = (file) => new Promise((resolve) => {
  const url = URL.createObjectURL(file);
  const im = new window.Image();
  im.onload = () => { URL.revokeObjectURL(url); resolve({ w: im.naturalWidth, h: im.naturalHeight }); };
  im.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
  im.src = url;
});
const validateFile = async (file) => {
  if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) return "Only JPG, PNG or WEBP";
  if (file.size > 10 * 1024 * 1024) return "Larger than 10 MB";
  const d = await readDims(file);
  if (!d) return "Could not read image";
  if (!ASPECT_OK(d.w, d.h)) return `Not 9:16 (${d.w}×${d.h})`;
  if (d.h < 1280) return `Too small (${d.w}×${d.h}), min 1280px tall`;
  return null;
};

function BulkUploadZone({ imgs, defaultNumber, onDone }) {
  const [number, setNumber] = useState(defaultNumber);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const inputRef = React.useRef(null);
  useEffect(() => { setNumber(defaultNumber); }, [defaultNumber]);
  const free = imgs.filter((i) => i.numerology_category === number && i.source !== "upload").length;

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (files.length > free) { toast.error(`Only ${free} un-replaced slot${free === 1 ? "" : "s"} left for Number ${number}. Drop up to ${free} files.`); return; }
    const rejected = [];
    const accepted = [];
    for (const f of files) {
      const err = await validateFile(f);
      if (err) rejected.push({ filename: f.name, ok: false, error: err }); else accepted.push(f);
    }
    if (!accepted.length) { setReport({ results: rejected }); toast.error("No valid files to upload"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      accepted.forEach((f) => fd.append("files", f));
      const { data } = await api.post(`/admin/images/bulk-upload?numerology_category=${number}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const results = [...data.results, ...rejected];
      setReport({ results, remaining: data.remaining_slots });
      const okCount = results.filter((r) => r.ok).length;
      okCount ? toast.success(`${okCount} image${okCount === 1 ? "" : "s"} placed into Number ${number}`) : toast.error("Upload failed");
      onDone();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass-panel rounded-2xl p-4 mb-6" data-testid="bulk-upload-zone">
      <div className="flex flex-wrap items-center gap-3 mb-3">
        <span className="text-xs uppercase tracking-widest text-gold">Bulk upload · Number</span>
        <select value={number} onChange={(e) => setNumber(Number(e.target.value))} className="bg-card-warm border border-gold rounded px-3 py-1.5 text-ivory text-sm" data-testid="bulk-number-select">
          {[1,2,3,4,5,6,7,8,9].map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
        <span className="text-xs text-muted-warm" data-testid="bulk-free-slots">{free} of 6 slots open · fills V0→V5 in order, skips slots you already replaced</span>
      </div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handleFiles(e.dataTransfer.files); }}
        onClick={() => !busy && inputRef.current?.click()}
        className={`rounded-xl border-2 border-dashed px-6 py-8 text-center cursor-pointer transition-colors ${drag ? "border-gold-hi bg-gold/10" : "border-gold/40 hover:border-gold"} ${busy ? "opacity-60 pointer-events-none" : ""}`}
        data-testid="bulk-dropzone"
      >
        <input ref={inputRef} type="file" multiple accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }} data-testid="bulk-file-input" />
        <div className="text-gold-hi font-serif-lux text-lg">{busy ? "Uploading…" : `Drop up to ${free} files here`}</div>
        <div className="text-xs text-muted-warm mt-1">or click to choose · 9:16 portrait · JPG/PNG/WEBP · ≤10 MB · ≥1280px tall</div>
      </div>
      {report && (
        <ul className="mt-3 space-y-1 text-xs" data-testid="bulk-report">
          {report.results.map((r, i) => (
            <li key={`${r.filename}-${i}`} className={r.ok ? "text-green-300" : "text-red-300"}>
              {r.ok ? "✓" : "✕"} {r.filename} {r.ok ? `→ N${number}/V${r.variant_index}` : `— ${r.error}`}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PhonePreviewModal({ img, onClose }) {
  const now = new Date();
  const time = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false });
  const date = now.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  return (
    <div className="fixed inset-0 z-50 bg-obsidian/90 backdrop-blur-md flex items-center justify-center px-4" onClick={onClose} data-testid="phone-preview-modal">
      <div onClick={(e) => e.stopPropagation()} className="flex flex-col items-center gap-4">
        <div className="relative w-[300px] aspect-[9/19.5] rounded-[3rem] bg-black border-[6px] border-[#1c1a17] shadow-[0_30px_90px_rgba(0,0,0,0.9),0_0_0_1px_rgba(214,170,69,0.35)] overflow-hidden" data-testid="phone-frame">
          <img src={resolveImg(img.image_url)} alt={img.deity} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/50" />
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full" />
          <div className="absolute top-3 left-6 text-[10px] text-white/90 font-medium">{time}</div>
          <div className="absolute top-3 right-6 flex items-center gap-1 text-[10px] text-white/90">▲ ⦿ ▮</div>
          <div className="absolute top-16 inset-x-0 text-center text-white drop-shadow-lg">
            <div className="text-[64px] font-light leading-none tracking-tight">{time}</div>
            <div className="text-xs mt-2 opacity-90">{date}</div>
          </div>
          <div className="absolute bottom-16 inset-x-6 rounded-2xl bg-white/15 backdrop-blur-md px-3 py-2 text-white">
            <div className="text-[9px] uppercase tracking-widest opacity-70">SHREE RAGO</div>
            <div className="text-xs font-medium truncate">{img.deity} · {img.theme}</div>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-24 h-1 rounded-full bg-white/70" />
        </div>
        <div className="text-center">
          <div className="text-[10px] text-gold uppercase tracking-widest">Slot N{img.numerology_category} / V{img.variant_index}</div>
          <div className="text-sm text-ivory">{img.deity}</div>
          <div className="text-[11px] text-muted-warm">{img.width && img.height ? `${img.width}×${img.height}px` : "Lock-screen preview"}</div>
        </div>
        <button onClick={onClose} className="btn-outline-gold text-xs px-5 py-2" data-testid="phone-preview-close">Close</button>
      </div>
    </div>
  );
}

function ImageSlotCard({ img, onToggle, onEdit, onPreview, onChanged }) {
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const err = await validateFile(file);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/images/${img.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`N${img.numerology_category}/V${img.variant_index} replaced`);
      onChanged();
    } catch (err2) {
      toast.error(err2?.response?.data?.detail || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="glass-panel rounded-xl overflow-hidden group" data-testid={`image-slot-${img.numerology_category}-${img.variant_index}`}>
      <div className="aspect-[9/16] bg-card-warm relative">
        <img src={resolveImg(img.image_url)} alt={img.deity} onClick={onPreview} className="w-full h-full object-cover cursor-zoom-in" data-testid={`image-slot-img-${img.numerology_category}-${img.variant_index}`} />
        {img.source === "upload" && (
          <span className="absolute top-1.5 left-1.5 text-[9px] uppercase tracking-widest bg-gold text-obsidian px-1.5 py-0.5 rounded" data-testid={`image-slot-badge-${img.numerology_category}-${img.variant_index}`}>Own</span>
        )}
        {busy && (
          <div className="absolute inset-0 bg-obsidian/80 flex items-center justify-center text-gold-hi text-xs tracking-widest uppercase animate-pulse">Uploading…</div>
        )}
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid={`image-slot-file-${img.numerology_category}-${img.variant_index}`} />
        <button onClick={() => inputRef.current?.click()} disabled={busy}
          className="absolute bottom-1.5 inset-x-1.5 btn-gold text-[10px] py-1.5 opacity-90 md:opacity-0 md:group-hover:opacity-100 transition-opacity disabled:opacity-40"
          data-testid={`image-slot-replace-${img.numerology_category}-${img.variant_index}`}>
          Replace
        </button>
      </div>
      <div className="p-2">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-gold uppercase tracking-widest">N{img.numerology_category}/V{img.variant_index}</div>
          <div className="flex gap-2">
            <button onClick={onPreview} className="text-[10px] text-muted-warm hover:text-gold-hi underline" data-testid={`image-slot-preview-${img.numerology_category}-${img.variant_index}`}>Preview</button>
            <button onClick={onEdit} className="text-[10px] text-muted-warm hover:text-gold-hi underline" data-testid={`image-slot-edit-${img.numerology_category}-${img.variant_index}`}>Edit</button>
          </div>
        </div>
        <div className="text-xs text-ivory truncate" data-testid={`image-slot-deity-${img.numerology_category}-${img.variant_index}`}>{img.deity}</div>
        <div className="text-[10px] text-muted-warm truncate">{img.theme}</div>
        <button onClick={onToggle} className={`mt-1 text-[10px] px-2 py-1 rounded ${img.active ? "bg-green-900/40 text-green-300" : "bg-red-900/40 text-red-300"}`} data-testid={`image-slot-toggle-${img.numerology_category}-${img.variant_index}`}>
          {img.active ? "Active" : "Inactive"}
        </button>
      </div>
    </div>
  );
}

function ImageEditModal({ img, onClose, onSaved }) {
  const [deity, setDeity] = useState(img.deity);
  const [theme, setTheme] = useState(img.theme);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/admin/images/${img.id}`, { deity, theme });
      toast.success("Saved");
      onSaved();
    } catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-obsidian/85 backdrop-blur-sm flex items-center justify-center px-4" onClick={onClose} data-testid="image-edit-modal">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="glass-panel rounded-2xl p-6 w-full max-w-sm space-y-4">
        <div>
          <div className="text-[10px] text-gold uppercase tracking-widest">Slot N{img.numerology_category} / V{img.variant_index}</div>
          <h3 className="font-serif-lux text-lg text-gold-hi">Edit Image Details</h3>
        </div>
        <label className="block">
          <span className="text-xs uppercase text-gold tracking-widest">Deity name</span>
          <input data-testid="image-edit-deity-input" value={deity} onChange={(e) => setDeity(e.target.value)} className="w-full mt-1.5 bg-card-warm border border-gold rounded-lg px-3 py-2 text-ivory text-sm" required />
        </label>
        <label className="block">
          <span className="text-xs uppercase text-gold tracking-widest">Theme</span>
          <input data-testid="image-edit-theme-input" value={theme} onChange={(e) => setTheme(e.target.value)} className="w-full mt-1.5 bg-card-warm border border-gold rounded-lg px-3 py-2 text-ivory text-sm" required />
        </label>
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onClose} className="btn-outline-gold text-xs px-4 py-2" data-testid="image-edit-cancel-btn">Cancel</button>
          <button disabled={busy} className="btn-gold text-xs px-4 py-2 disabled:opacity-60" data-testid="image-edit-save-btn">{busy ? "…" : "Save"}</button>
        </div>
      </form>
    </div>
  );
}

function Testimonials() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ first_name: "", city: "", state: "", language: "en", short_quote: "", verified_purchase: false });
  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setRows(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const add = async (e) => {
    e.preventDefault();
    await api.post("/admin/testimonials", form);
    setForm({ first_name: "", city: "", state: "", language: "en", short_quote: "", verified_purchase: false });
    toast.success("Testimonial added");
    load();
  };
  const patch = async (id, body, msg) => { await api.patch(`/admin/testimonials/${id}`, body); msg && toast.success(msg); load(); };
  const remove = async (id) => { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/admin/testimonials/${id}`); load(); };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="glass-panel rounded-2xl p-4 grid grid-cols-1 sm:grid-cols-6 gap-3" data-testid="testimonial-form">
        <input value={form.first_name} onChange={(e)=>setForm({...form, first_name: e.target.value})} placeholder="First name" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" required data-testid="tm-first-name" />
        <input value={form.city} onChange={(e)=>setForm({...form, city: e.target.value})} placeholder="City" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" required data-testid="tm-city" />
        <input value={form.state} onChange={(e)=>setForm({...form, state: e.target.value})} placeholder="State" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" data-testid="tm-state" />
        <select value={form.language} onChange={(e)=>setForm({...form, language: e.target.value})} className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" data-testid="tm-language">
          {LANGUAGES.map(l => <option key={l.code} value={l.code}>{l.name}</option>)}
        </select>
        <input value={form.short_quote} onChange={(e)=>setForm({...form, short_quote: e.target.value})} placeholder="Short quote" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm sm:col-span-2" required data-testid="tm-quote" />
        <label className="flex items-center gap-2 text-xs text-muted-warm sm:col-span-2"><input type="checkbox" checked={form.verified_purchase} onChange={(e)=>setForm({...form, verified_purchase: e.target.checked})} data-testid="tm-verified" /> Verified purchase</label>
        <button className="btn-gold text-xs sm:col-span-4" data-testid="add-testimonial-btn">Add Testimonial</button>
      </form>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((tm) => (
          <div key={tm.id} className="glass-panel rounded-xl overflow-hidden" data-testid={`tm-card-${tm.id}`}>
            <div className="aspect-[4/3] bg-card-warm relative">
              {tm.video_url ? (
                <video src={resolveImg(tm.video_url)} poster={tm.thumbnail_url ? resolveImg(tm.thumbnail_url) : undefined} controls playsInline preload="metadata" className="w-full h-full object-cover" data-testid={`tm-video-${tm.id}`} />
              ) : tm.thumbnail_url ? (
                <img src={resolveImg(tm.thumbnail_url)} alt={tm.first_name} className="w-full h-full object-cover" data-testid={`tm-photo-${tm.id}`} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-xs text-muted-warm">No photo yet</div>
              )}
              <span className={`absolute top-2 left-2 text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${tm.published ? "bg-green-900/70 text-green-300" : "bg-red-900/70 text-red-300"}`}>{tm.published ? "Published" : "Hidden"}</span>
            </div>
            <div className="p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-gold-hi text-sm">{tm.first_name} · {tm.city}{tm.state ? `, ${tm.state}` : ""}</div>
                  <div className="text-[10px] uppercase text-muted-warm">{tm.language}{tm.verified_purchase ? " · ✓ verified" : ""}</div>
                </div>
                <button onClick={() => remove(tm.id)} className="text-red-300 hover:text-red-200" title="Delete" data-testid={`tm-delete-${tm.id}`}><Trash2 size={14} /></button>
              </div>
              <p className="text-ivory text-sm mt-2 line-clamp-3">{tm.short_quote}</p>
              <div className="grid grid-cols-2 gap-2 mt-3">
                <MediaDropzone accept="image" folder="testimonials" compact label="Photo" onUploaded={(d) => patch(tm.id, { thumbnail_url: d.url }, "Photo updated")} testId={`tm-photo-upload-${tm.id}`} />
                <MediaDropzone accept="video" folder="testimonials" compact label="Video" onUploaded={(d) => patch(tm.id, { video_url: d.url }, "Video attached")} testId={`tm-video-upload-${tm.id}`} />
              </div>
              <div className="flex gap-3 mt-2 text-[11px]">
                <button onClick={() => patch(tm.id, { published: !tm.published })} className="text-muted-warm hover:text-gold-hi underline" data-testid={`tm-toggle-${tm.id}`}>{tm.published ? "Hide" : "Publish"}</button>
                <button onClick={() => patch(tm.id, { featured: !tm.featured })} className="text-muted-warm hover:text-gold-hi underline" data-testid={`tm-feature-${tm.id}`}>{tm.featured ? "Unfeature" : "Feature"}</button>
                {tm.video_url && <button onClick={() => patch(tm.id, { video_url: "" }, "Video removed")} className="text-muted-warm hover:text-red-300 underline" data-testid={`tm-remove-video-${tm.id}`}>Remove video</button>}
              </div>
            </div>
          </div>
        ))}
        {rows.length === 0 && <div className="text-muted-warm text-sm">No testimonials yet.</div>}
      </div>
    </div>
  );
}

function Campaigns() {
  const base = window.location.origin;
  const [utm, setUtm] = useState({ source: "meta", medium: "cpc", campaign: "" });
  const [copied, setCopied] = useState("");

  const copy = (url) => {
    navigator.clipboard.writeText(url);
    setCopied(url);
    toast.success("Copied to clipboard");
    setTimeout(() => setCopied(""), 1500);
  };

  return (
    <div className="space-y-6">
      <div className="glass-panel rounded-2xl p-4">
        <h3 className="font-serif-lux text-lg text-gold-hi mb-4">Campaign URL Builder</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <input value={utm.source} onChange={(e)=>setUtm({...utm, source: e.target.value})} placeholder="utm_source" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" />
          <input value={utm.medium} onChange={(e)=>setUtm({...utm, medium: e.target.value})} placeholder="utm_medium" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" />
          <input value={utm.campaign} onChange={(e)=>setUtm({...utm, campaign: e.target.value})} placeholder="utm_campaign" className="bg-card-warm border border-gold rounded px-3 py-2 text-ivory text-sm" />
        </div>
      </div>

      <div className="glass-panel rounded-2xl p-4 overflow-x-auto">
        <h3 className="font-serif-lux text-lg text-gold-hi mb-4">Language Campaigns</h3>
        <table className="w-full text-sm">
          <thead className="text-xs uppercase text-muted-warm">
            <tr><th className="text-left py-2">Language</th><th className="text-left">URL</th><th></th></tr>
          </thead>
          <tbody className="text-ivory">
            {LANGUAGES.map((l) => {
              const params = new URLSearchParams();
              if (utm.source) params.set("utm_source", utm.source);
              if (utm.medium) params.set("utm_medium", utm.medium);
              if (utm.campaign) params.set("utm_campaign", utm.campaign);
              const qs = params.toString();
              const url = `${base}/${l.code}${qs ? `?${qs}` : ""}`;
              return (
                <tr key={l.code} className="border-t border-gold/20">
                  <td className="py-2">{l.native} ({l.code})</td>
                  <td className="text-xs text-muted-warm truncate max-w-md">{url}</td>
                  <td>
                    <button data-testid={`copy-${l.code}-url`} onClick={() => copy(url)} className="btn-outline-gold text-[10px] px-3 py-1">
                      {copied === url ? "Copied ✓" : "Copy"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
